/* God Console — seed/mock data (mirrors D1 / Autumn / ledger shapes).
   Live wiring (admin endpoints) replaces these incrementally. */

export const PULSE = [
  { label: "Total users", value: "8,412", delta: "+3.2%", up: true, icon: "users" },
  { label: "Active orgs", value: "312", delta: "+5", up: true, icon: "building-2" },
  { label: "MRR", value: "$48.2k", delta: "+6.1%", up: true, icon: "banknote" },
  { label: "Spend today", value: "$1,556", sub: "of $2,000 cap", icon: "flame", warn: true },
  { label: "Blended margin", value: "57%", delta: "-2pt", up: false, icon: "percent" },
  { label: "Live lessons", value: "146", sub: "in progress now", icon: "graduation-cap" },
  { label: "Error rate", value: "0.4%", sub: "last 1h", icon: "alert-triangle" },
  { label: "Free→Paid", value: "11.3%", delta: "+0.8pt", up: true, icon: "trending-up" },
] as const;

export const PROVIDERS = [
  { name: "Anthropic", status: "ok", latency: "820ms" },
  { name: "Cartesia TTS", status: "ok", latency: "240ms" },
  { name: "AssemblyAI", status: "degraded", latency: "1.9s" },
  { name: "Autumn / Stripe", status: "ok", latency: "180ms" },
  { name: "WorkOS", status: "ok", latency: "150ms" },
  { name: "Cloudflare", status: "ok", latency: "40ms" },
] as const;

export const ATTENTION = [
  { ic: "alert-triangle", tone: "warn", t: "AssemblyAI latency elevated (1.9s p95)", d: "STT degraded for ~12 min · 3 sessions affected" },
  { ic: "flame", tone: "danger", t: "Global spend at 78% of daily cap by 2pm", d: "Opus usage up 22% d/d — consider tightening org caps" },
  { ic: "user-x", tone: "neutral", t: "2 orgs past due on invoices", d: "Northwind, Vela — dunning in progress" },
] as const;

export type GodUser = {
  id: string; orgId?: string; name: string; email: string; org: string; role: string; plan: string;
  status: string; last: string; turns: number; cost: number; margin: number;
  credits: string; mastery: string; initials: string;
};
export const USERS: GodUser[] = [
  { id: "u1", name: "Alex Chen", email: "alex@adaptr.studio", org: "Adaptr Studio", role: "Owner", plan: "Pro", status: "active", last: "2m", turns: 1240, cost: 18.4, margin: 62, credits: "360", mastery: "4/9", initials: "AC" },
  { id: "u2", name: "Jordan Lee", email: "jordan@adaptr.studio", org: "Adaptr Studio", role: "Admin", plan: "Pro", status: "active", last: "1h", turns: 980, cost: 14.1, margin: 58, credits: "1,200", mastery: "3/9", initials: "JL" },
  { id: "u3", name: "Sam Rivera", email: "sam@northwind.io", org: "Northwind", role: "Member", plan: "Team", status: "active", last: "3h", turns: 2310, cost: 39.8, margin: 49, credits: "5,400", mastery: "6/9", initials: "SR" },
  { id: "u4", name: "Priya Nair", email: "priya@northwind.io", org: "Northwind", role: "Member", plan: "Team", status: "active", last: "1d", turns: 410, cost: 6.2, margin: 66, credits: "9,000", mastery: "2/9", initials: "PN" },
  { id: "u5", name: "Marco Diaz", email: "marco@lumen.app", org: "Lumen Labs", role: "Owner", plan: "Founder", status: "active", last: "5m", turns: 5120, cost: 92.3, margin: 71, credits: "∞", mastery: "9/9", initials: "MD" },
  { id: "u6", name: "Tess Okafor", email: "tess@vela.co", org: "Vela", role: "Owner", plan: "Free", status: "active", last: "2d", turns: 120, cost: 2.1, margin: 31, credits: "40", mastery: "1/9", initials: "TO" },
  { id: "u7", name: "Ken Watanabe", email: "ken@orbital.dev", org: "Orbital", role: "Admin", plan: "Pro", status: "active", last: "30m", turns: 760, cost: 11.9, margin: 60, credits: "800", mastery: "5/9", initials: "KW" },
  { id: "u8", name: "Lia Fonseca", email: "lia@vela.co", org: "Vela", role: "Member", plan: "Free", status: "suspended", last: "9d", turns: 60, cost: 0.9, margin: 22, credits: "0", mastery: "0/9", initials: "LF" },
];

export const USER_LESSONS = [
  { name: "Figma AI — first draft to polish", state: "mastered", fade: "L4" },
  { name: "Claude for design critique", state: "active", fade: "L2" },
  { name: "Prompting fundamentals", state: "mastered", fade: "L4" },
  { name: "Context engineering", state: "active", fade: "L1" },
  { name: "Evaluating AI output", state: "ready", fade: "—" },
  { name: "Designing agent workflows", state: "locked", fade: "—" },
] as const;

export const USER_MEMORY = [
  { kind: "role", text: "Senior Product Designer at Adaptr Studio", conf: 0.96 },
  { kind: "stack", text: "Figma, Linear, Notion, GitHub", conf: 0.92 },
  { kind: "preference", text: "Prefers Sonnet for quick asks, Opus for planning", conf: 0.81 },
  { kind: "goal", text: "Wants to lead AI-assisted design ops", conf: 0.88 },
  { kind: "schedule", text: "Deep-focus mornings, 9–6 PT", conf: 0.74 },
] as const;

export type GodOrg = {
  id: string; name: string; domain: string; plan: string; seats: string; seatLimit: number | null;
  mrr: string; margin: number | null; status: string; owner: string; created: string;
  spendToday: number; dailyCap: number | null; sso: boolean;
};
export const ORGS: GodOrg[] = [
  { id: "o1", name: "Adaptr Studio", domain: "adaptr.studio", plan: "Pro", seats: "3/5", seatLimit: 5, mrr: "$40", margin: 60, status: "active", owner: "alex@adaptr.studio", created: "Jan 2026", spendToday: 32, dailyCap: 120, sso: false },
  { id: "o2", name: "Northwind", domain: "northwind.io", plan: "Team", seats: "8/10", seatLimit: 10, mrr: "$160", margin: 52, status: "past_due", owner: "sam@northwind.io", created: "Nov 2025", spendToday: 88, dailyCap: 200, sso: true },
  { id: "o3", name: "Lumen Labs", domain: "lumen.app", plan: "Founder", seats: "∞", seatLimit: null, mrr: "$0", margin: null, status: "comp", owner: "marco@lumen.app", created: "Feb 2026", spendToday: 94, dailyCap: null, sso: false },
  { id: "o4", name: "Vela", domain: "vela.co", plan: "Free", seats: "2", seatLimit: 2, mrr: "$0", margin: 26, status: "past_due", owner: "tess@vela.co", created: "Mar 2026", spendToday: 3, dailyCap: 20, sso: false },
  { id: "o5", name: "Orbital", domain: "orbital.dev", plan: "Pro", seats: "4/5", seatLimit: 5, mrr: "$80", margin: 58, status: "active", owner: "ken@orbital.dev", created: "Dec 2025", spendToday: 21, dailyCap: 120, sso: false },
];

export const ORG_SHARED = [
  { ic: "brain", name: "AI Prototyping Fundamentals", assigned: 4, pct: 68 },
  { ic: "wrench", name: "Tool mastery: Figma AI", assigned: 3, pct: 42 },
] as const;

export const PLANS_LIST = [
  { name: "Free", price: "$0 / mo", credits: "100 credits / mo" },
  { name: "Pro", price: "$20 / mo", credits: "5,000 credits / mo" },
  { name: "Team", price: "$18 / seat", credits: "8,000 / seat" },
  { name: "Founder", price: "Comp", credits: "Unlimited" },
] as const;

export const USER_SESSIONS = [
  { device: "MacBook Pro · Orbit 2.4.1", os: "macOS 15.2", loc: "San Francisco, US", ip: "24.5.18.x", last: "active now", current: true },
  { device: "Web · Chrome 126", os: "macOS", loc: "San Francisco, US", ip: "24.5.18.x", last: "2h ago" },
  { device: "iPhone 15 · Orbit iOS", os: "iOS 18.1", loc: "Oakland, US", ip: "73.2.44.x", last: "1d ago" },
] as const;

export const USER_ACTIVITY = [
  { ic: "log-in", t: "Signed in", d: "Web · Chrome 126", when: "2m ago" },
  { ic: "graduation-cap", t: "Completed — Prompting fundamentals", d: "checkpoint passed", when: "1h ago" },
  { ic: "zap", t: "Used 240 credits", d: "Opus · planning session", when: "3h ago" },
  { ic: "credit-card", t: "Bought 2,500 credits", d: "$11.00 · Visa ••4242", when: "Yesterday" },
  { ic: "venetian-mask", t: "Impersonated by ops@adaptr.ai", d: "debug stuck lesson", when: "2 days ago", flag: true },
  { ic: "user-plus", t: "Joined Adaptr Studio", d: "invited by jordan@adaptr.studio", when: "Jan 2026" },
] as const;

export const SPEND = [
  { name: "Anthropic — Sonnet", val: 720, cls: "a-sonnet" },
  { name: "Anthropic — Opus", val: 520, cls: "a-opus" },
  { name: "Cartesia TTS", val: 180, cls: "a-tts" },
  { name: "AssemblyAI STT", val: 96, cls: "a-stt" },
  { name: "Cloudflare", val: 40, cls: "a-cf" },
] as const;

export const TURNS = [
  { time: "14:02:11", user: "marco@lumen.app", model: "Opus", tok: "18.2k", cost: "$0.34", cr: 3 },
  { time: "14:02:08", user: "sam@northwind.io", model: "Sonnet", tok: "6.1k", cost: "$0.04", cr: 1 },
  { time: "14:01:59", user: "alex@adaptr.studio", model: "Sonnet", tok: "9.4k", cost: "$0.07", cr: 1 },
  { time: "14:01:51", user: "ken@orbital.dev", model: "Opus", tok: "22.0k", cost: "$0.41", cr: 3 },
  { time: "14:01:47", user: "priya@northwind.io", model: "Sonnet", tok: "3.2k", cost: "$0.02", cr: 1 },
  { time: "14:01:39", user: "marco@lumen.app", model: "Sonnet", tok: "7.8k", cost: "$0.05", cr: 1 },
] as const;

export const RATECARD = [
  { item: "Claude Sonnet — input", rate: "$3.00 / M tok" },
  { item: "Claude Sonnet — output", rate: "$15.00 / M tok" },
  { item: "Claude Opus — input", rate: "$15.00 / M tok" },
  { item: "Claude Opus — output", rate: "$75.00 / M tok" },
  { item: "Cartesia TTS", rate: "$0.020 / 1k char" },
  { item: "AssemblyAI STT", rate: "$0.0025 / sec" },
] as const;

export const CREDIT_WEIGHTS = [
  { model: "Sonnet", weight: 1 },
  { model: "Opus", weight: 3 },
] as const;

export const LESSONS = [
  { title: "Figma AI — first draft to polish", tool: "Figma", diff: "Core", role: "Designer", status: "live" },
  { title: "Claude for design critique", tool: "Claude", diff: "Core", role: "Designer", status: "live" },
  { title: "Cursor for design tokens", tool: "Cursor", diff: "Intermediate", role: "Designer", status: "live" },
  { title: "Linear AI triage", tool: "Linear", diff: "Core", role: "PM", status: "live" },
  { title: "Prompting fundamentals", tool: "—", diff: "Core", role: "All", status: "live" },
  { title: "Context engineering", tool: "—", diff: "Core", role: "All", status: "live" },
  { title: "Evaluating AI output", tool: "—", diff: "Intermediate", role: "All", status: "draft" },
  { title: "Designing agent workflows", tool: "Cursor", diff: "Advanced", role: "Eng", status: "disabled" },
] as const;

export const PROMPT_TEXT = `# Orbit — Coach system prompt  (v14, live)
ROLE: You are Orbit, a calm on-screen AI coach.

VOICE
- Speak in short, spoken-style sentences. Never read UI verbatim.
- One instruction at a time. Wait for the user to act.

FADE LADDER (current: {{fade}})
  L1  POINT  → name the element + why
  L2  TYPE   → give the exact words to type
  L3  HINT   → nudge, let them find it
  L4  COMPLETE → silent unless they stall 8s

TASK FRAMING
- Anchor every step to {{lesson.objective}}.
- Bind tools to {{profile.stack}} only.`;

export const MEM_KINDS = ["role", "stack", "preference", "goal", "schedule", "project", "tool_skill"] as const;
export const MEM_ROWS = [
  { user: "alex@adaptr.studio", kind: "role", text: "Senior Product Designer", conf: 0.96 },
  { user: "sam@northwind.io", kind: "goal", text: "Ship AI triage for support", conf: 0.84 },
  { user: "marco@lumen.app", kind: "preference", text: "Always wants Opus for planning", conf: 0.9 },
  { user: "ken@orbital.dev", kind: "stack", text: "Cursor, GitHub, Linear", conf: 0.79 },
  { user: "priya@northwind.io", kind: "schedule", text: "Async, EU hours", conf: 0.71 },
] as const;

export const FEED = [
  { t: "14:02:11", m: "POST /chat", s: 200, ms: 840, u: "marco@lumen.app" },
  { t: "14:02:08", m: "POST /chat", s: 200, ms: 610, u: "sam@northwind.io" },
  { t: "14:02:02", m: "POST /stt", s: 200, ms: 1920, u: "ken@orbital.dev" },
  { t: "14:01:58", m: "POST /chat", s: 429, ms: 12, u: "lia@vela.co" },
  { t: "14:01:51", m: "POST /tts", s: 200, ms: 240, u: "alex@adaptr.studio" },
  { t: "14:01:44", m: "GET /me", s: 200, ms: 30, u: "priya@northwind.io" },
] as const;

export const RELEASES = [
  { v: "2.4.1", ch: "stable", adoption: 74, status: "live", date: "Jun 14" },
  { v: "2.4.0", ch: "stable", adoption: 21, status: "live", date: "Jun 2" },
  { v: "2.5.0-beta.3", ch: "beta", adoption: 5, status: "beta", date: "Jun 16" },
  { v: "2.3.9", ch: "stable", adoption: 0, status: "killed", date: "May 20" },
] as const;

export const FLAGS = [
  { key: "opus_enabled", desc: "Allow Opus model selection", scope: "global", on: true },
  { key: "tts_enabled", desc: "Cartesia voice output", scope: "global", on: true },
  { key: "memory_extraction", desc: "Write to user_memory after /chat", scope: "global", on: false },
  { key: "maintenance_mode", desc: "Read-only service banner", scope: "global", on: false },
  { key: "new_onboarding", desc: "Voice interview v2 rollout", scope: "25% users", on: true },
] as const;

export const AUDIT = [
  { who: "ops@adaptr.ai", action: "granted 5,000 credits", target: "vela.co", when: "13:50", reason: "support comp" },
  { who: "ops@adaptr.ai", action: "impersonated", target: "marco@lumen.app", when: "11:22", reason: "debug stuck lesson" },
  { who: "ops@adaptr.ai", action: "changed plan → Founder", target: "lumen.app", when: "Yesterday", reason: "design partner" },
  { who: "ops@adaptr.ai", action: "disabled lesson", target: "Designing agent workflows", when: "Yesterday", reason: "bad check logic" },
  { who: "ops@adaptr.ai", action: "deleted memory entry", target: "lia@vela.co", when: "2 days ago", reason: "GDPR request" },
] as const;
